import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertCircle, UserPlus, Send, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { createResourceNoAuth, inviteResource, linkResourceToAuthUser } from '../lib/resources';
import { getResourceRoles, ResourceRole } from '../lib/resourceRoles';
import { useAppSystem } from '../hooks/useAppSystem';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';

interface ResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId?: string;
  /** Pre-select "This is me — link to my admin login" (setup wizard just-me flow). */
  defaultLinkToAdmin?: boolean;
}

export default function ResourceModal({ isOpen, onClose, onSuccess, businessId, defaultLinkToAdmin = false }: ResourceModalProps) {
  const { coreApps, installedAddons } = useAppSystem();
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permissionRoles, setPermissionRoles] = useState<ResourceRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  // "This is me" — link the new resource to the owner's existing admin login.
  // Only offered to the business owner, and only while no resource is linked
  // to their account yet (one linked profile max).
  const [linkToAdmin, setLinkToAdmin] = useState(false);
  const [adminLinkAvailable, setAdminLinkAvailable] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role_id: '', // Permission role ID
    compensation_type: 'hourly' as 'hourly' | 'flat_rate' | 'commission',
    hourly_rate: '0',
    flat_rate_amount: '0',
    commission_percentage: '0',
    status: 'active',
    is_admin: false,
    password: '',
    confirm_password: '',
    notes: ''
  });

  // Check if Payroll app is installed
  const isPayrollAppInstalled = coreApps.some(app => app.app?.slug === 'payroll') || 
                                installedAddons.some(app => app.app?.slug === 'payroll');

  const loadPermissionRoles = async () => {
    if (!businessId) return;
    
    try {
      const roles = await getResourceRoles(businessId);
      setPermissionRoles(roles);
    } catch (err) {
      console.error('Error loading permission roles:', err);
      setError(err instanceof Error ? err.message : 'Failed to load permission roles');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadPermissionRoles();
    }
  }, [isOpen, businessId]);

  // Offer "link to my admin login" only to the business owner, and only when
  // no resource in this business is linked to their account yet.
  useEffect(() => {
    if (!isOpen || !businessId || !user?.id || !supabase) {
      setAdminLinkAvailable(false);
      return;
    }
    const isOwner = selectedBusiness?.owner_id === user.id;
    if (!isOwner) {
      setAdminLinkAvailable(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('resources')
          .select('id')
          .eq('business_id', businessId)
          .eq('auth_user_id', user.id)
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        const available = !data;
        setAdminLinkAvailable(available);
        // Pre-select the link option when requested (setup wizard just-me flow).
        if (available && defaultLinkToAdmin) {
          setLinkToAdmin(true);
          setFormData(prev => ({ ...prev, email: user.email || prev.email, is_admin: true }));
        }
      } catch {
        if (!cancelled) setAdminLinkAvailable(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, businessId, user?.id, selectedBusiness?.owner_id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Reset form data when modal closes
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role_id: '',
        compensation_type: 'hourly',
        hourly_rate: '0',
        flat_rate_amount: '0',
        commission_percentage: '0',
        status: 'active',
        is_admin: false,
        password: '',
        confirm_password: '',
        notes: ''
      });
      setError(null);
      setLinkToAdmin(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (mode: 'create' | 'invite', e?: React.FormEvent) => {
    e?.preventDefault();
    console.log('🔍 [DEBUG] Starting team member creation process');
    console.log('🔍 [DEBUG] Business ID:', businessId);
    console.log('🔍 [DEBUG] Form data:', formData);
    
    if (!businessId) {
      console.error('❌ [DEBUG] No business selected');
      setError('No business selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('🔍 [DEBUG] Starting validation...');

      // Basic validation
      if (!formData.first_name.trim()) {
        console.error('❌ [DEBUG] First name validation failed');
        throw new Error('First name is required');
      }
      if (!formData.last_name.trim()) {
        console.error('❌ [DEBUG] Last name validation failed');
        throw new Error('Last name is required');
      }
      if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        console.error('❌ [DEBUG] Email validation failed:', formData.email);
        throw new Error('Valid email is required');
      }

      console.log('✅ [DEBUG] All validations passed');

      // Initialize compensation values
      const compensationData = {
        hourly_rate: 0,
        flat_rate_amount: 0,
        commission_percentage: 0,
        commission_rate: 0
      };

      // Set compensation based on type
      if (formData.compensation_type === 'hourly') {
        compensationData.hourly_rate = parseFloat(formData.hourly_rate) || 0;
        console.log('🔍 [DEBUG] Setting hourly rate:', compensationData.hourly_rate);
      } else if (formData.compensation_type === 'flat_rate') {
        compensationData.flat_rate_amount = parseFloat(formData.flat_rate_amount) || 0;
        console.log('🔍 [DEBUG] Setting flat rate amount:', compensationData.flat_rate_amount);
      } else if (formData.compensation_type === 'commission') {
        compensationData.commission_percentage = parseFloat(formData.commission_percentage) || 0;
        compensationData.commission_rate = compensationData.commission_percentage / 100;
        console.log('🔍 [DEBUG] Setting commission percentage:', compensationData.commission_percentage);
      }

      console.log('🔍 [DEBUG] Compensation data:', compensationData);

      // Create the resource payload
      const resourcePayload = {
        business_id: businessId,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        role: 'DEFAULT', // Default value for legacy role column
        role_id: formData.role_id || undefined,
        status: formData.status,
        compensation_type: formData.compensation_type,
        // Admin-linked profiles are always is_admin — this is what makes
        // hasPermission() bypass the role's restrictions for the owner.
        is_admin: linkToAdmin ? true : formData.is_admin,
        earnings_ytd: 0,
        earnings_mtd: 0,
        earnings_last_month: 0,
        is_mobile_available: false,
        schedule_availability: {
          monday: { enabled: false, shifts: [] },
          tuesday: { enabled: false, shifts: [] },
          wednesday: { enabled: false, shifts: [] },
          thursday: { enabled: false, shifts: [] },
          friday: { enabled: false, shifts: [] },
          saturday: { enabled: false, shifts: [] },
          sunday: { enabled: false, shifts: [] }
        },
        ...compensationData
      };

      console.log('🔍 [DEBUG] Resource payload created:', resourcePayload);
      console.log('🔍 [DEBUG] About to call createResource...');

      const result = await createResourceNoAuth(resourcePayload);
      console.log('✅ [DEBUG] createResourceNoAuth completed successfully:', result);

      if (linkToAdmin && user?.id) {
        // Sole-proprietor flow: this resource IS the admin — reuse their login.
        try {
          await linkResourceToAuthUser((result as any).id, user.id);
          toast.success('Team member created & linked to your admin login');
        } catch (linkErr: any) {
          console.error('Admin link failed:', linkErr);
          toast.error(`Created, but linking to your login failed: ${linkErr?.message || 'unknown error'}`);
        }
      } else if (mode === 'invite') {
        try {
          await inviteResource((result as any).id);
          toast.success('Team member created & invited');
        } catch (inviteErr: any) {
          console.error('Invite failed:', inviteErr);
          toast.error(`Created, but invite failed: ${inviteErr?.message || 'unknown error'}`);
        }
      } else {
        toast.success('Team member created');
      }

      onSuccess();
      console.log('✅ [DEBUG] onSuccess callback called');
    } catch (err) {
      console.error('❌ [DEBUG] Error creating resource:', err);
      console.error('❌ [DEBUG] Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        name: err instanceof Error ? err.name : undefined
      });
      setError(err instanceof Error ? err.message : 'Failed to create team member');
    } finally {
      console.log('🔍 [DEBUG] Setting loading to false');
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{
        zIndex: 100010,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[700px] lg:w-[800px] ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050,
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
          <h2 className="text-2xl font-bold text-gray-900" style={{ 
            letterSpacing: '-0.02em'
          }}>
            New Team Member
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Add a new team member to your business
          </p>
        </div>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto bg-white"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
            {error && (
              <div className="bg-red-50 border border-red-200/60 text-red-700 px-4 py-3 rounded-xl text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              </div>
            )}

            <form onSubmit={(e) => handleSubmit('create', e)} className="space-y-6">
              {/* Basic Information Section */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      disabled={linkToAdmin}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 disabled:bg-gray-50 disabled:text-gray-500"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter email address"
                    />
                    {linkToAdmin && (
                      <p className="mt-1 text-sm text-gray-500">Locked to your admin login email.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Permission Role
                    </label>
                    <select
                      value={formData.role_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, role_id: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                    >
                      <option value="">No specific permissions (optional)</option>
                      {permissionRoles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-sm text-gray-600">
                      {linkToAdmin
                        ? "Sets your role on the team — as the main admin you keep full access regardless."
                        : 'Assign system access permissions to this team member'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Compensation Section - Only show if Payroll app is installed */}
              {isPayrollAppInstalled && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Compensation</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Compensation Type
                      </label>
                      <select
                        value={formData.compensation_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, compensation_type: e.target.value as 'hourly' | 'flat_rate' | 'commission' }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      >
                        <option value="hourly">Hourly Rate</option>
                        <option value="flat_rate">Flat Rate</option>
                        <option value="commission">Commission</option>
                      </select>
                    </div>
                    
                    {formData.compensation_type === 'hourly' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Hourly Rate
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">$</span>
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.hourly_rate}
                            onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    )}
                    
                    {formData.compensation_type === 'flat_rate' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Flat Rate Amount
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">$</span>
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.flat_rate_amount}
                            onChange={(e) => setFormData(prev => ({ ...prev, flat_rate_amount: e.target.value }))}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    )}
                    
                    {formData.compensation_type === 'commission' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Commission Rate
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={formData.commission_percentage}
                            onChange={(e) => setFormData(prev => ({ ...prev, commission_percentage: e.target.value }))}
                            className="w-full pl-4 pr-8 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="0"
                          />
                          <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">%</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Access Section */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Access</h3>
                <div className="space-y-4">
                  {/* Link to main admin (sole proprietor "this is me") — offered
                      only to the owner while no resource is linked to them yet */}
                  {adminLinkAvailable && (
                    <div className="bg-purple-50 border border-purple-200/60 rounded-xl p-4">
                      <label className="flex items-start space-x-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={linkToAdmin}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setLinkToAdmin(checked);
                            if (checked) {
                              setFormData(prev => ({
                                ...prev,
                                email: user?.email || prev.email,
                                is_admin: true,
                              }));
                            }
                          }}
                          className="mt-1 h-5 w-5 text-purple-600 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 cursor-pointer"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-gray-900 group-hover:text-purple-700 transition-colors inline-flex items-center gap-1.5">
                            <Link2 className="h-4 w-4" />
                            This is me — link to my admin login
                          </span>
                          <p className="text-xs text-gray-600 mt-1">
                            Creates your own team member profile using your existing admin login — no separate
                            account needed. You'll be schedulable like any team member, and this still uses one
                            of your team member slots.
                          </p>
                        </div>
                      </label>
                    </div>
                  )}

                  {/* Admin Access Checkbox */}
                  <div className="bg-blue-50 border border-blue-200/60 rounded-xl p-4">
                    <label className="flex items-start space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={formData.is_admin}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_admin: e.target.checked }))}
                        className="mt-1 h-5 w-5 text-blue-600 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          Make Admin
                        </span>
                        <p className="text-xs text-gray-600 mt-1">
                          Grant full administrative access to all features and settings
                        </p>
                      </div>
                    </label>
                  </div>
                  
                  <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-[13px] text-gray-600">
                    {linkToAdmin ? (
                      <>This profile will use your existing admin login — no invite or password needed.</>
                    ) : (
                      <>No password needed here. Choose <strong>Create</strong> to add them without a login, or <strong>Create &amp; Invite</strong> to email them a signup link where they set their own password.</>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Notes</h3>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                  style={{ fontSize: '16px' }}
                  placeholder="Add any additional notes about this team member..."
                />
              </div>

              {/* Submit Buttons */}
              <div className="pt-4 flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => handleSubmit('create')}
                  disabled={loading}
                  className="flex-1 inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {loading ? (
                    <><div className="w-5 h-5 border-2 border-blue-300/40 border-t-blue-600 rounded-full animate-spin mr-2"></div>Working…</>
                  ) : (
                    <><UserPlus className="h-4 w-4 mr-2" /> Create</>
                  )}
                </button>
                {/* Inviting makes no sense for a profile linked to the admin's own login */}
                {!linkToAdmin && (
                  <button
                    type="button"
                    onClick={() => handleSubmit('invite')}
                    disabled={loading}
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {loading ? (
                      <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>Working…</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" /> Create &amp; Invite</>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree  
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}